import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { fullUrl } from "./ImageUpload";
import { ShieldCheck, FileCheck, Trash2, Clock, Paperclip } from "lucide-react";

const STATUS_META = {
  ricevuto: { label: "Ricevuto", cls: "bg-emerald-100 text-emerald-700" },
  in_attesa_cancellazione: { label: "In attesa cancellazione", cls: "bg-amber-100 text-amber-800" },
  documento_cancellato: { label: "Documento cancellato", cls: "bg-slate-200 text-slate-600" },
};

export default function GuestDocuments({ isAdmin }) {
  const [docs, setDocs] = useState([]);

  const load = useCallback(() => {
    api.get("/guest-documents").then((r) => setDocs(r.data)).catch(() => setDocs([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, payload) => {
    try { await api.put(`/guest-documents/${id}`, payload); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const purge = async () => {
    if (!window.confirm("Eseguire ora la pulizia dei documenti scaduti (retention check-out +1)?")) return;
    try { const r = await api.post("/guest-documents/purge-now"); toast.success(`Documenti cancellati: ${r.data.purged}`); load(); }
    catch { toast.error("Errore pulizia"); }
  };
  const fmt = (s) => s ? new Date(s).toLocaleDateString("it-IT") : "—";

  return (
    <div className="space-y-4" data-testid="guest-documents">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-xs text-slate-500"><ShieldCheck size={14} className="text-primary" /> Conservazione temporanea: i file vengono cancellati automaticamente al check-out +1 giorno. Restano solo i metadati di tracciabilità.</p>
        {isAdmin && <Button data-testid="gdoc-purge-btn" variant="outline" size="sm" onClick={purge} className="gap-2 text-rose-600"><Trash2 size={14} /> Pulisci scaduti ora</Button>}
      </div>
      <Card className="overflow-hidden border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="gdoc-table">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-3">Ospite</th>
                <th className="px-3 py-3">Ricevuto</th>
                <th className="px-3 py-3">Completo</th>
                <th className="px-3 py-3">Dati OK</th>
                <th className="px-3 py-3">Alloggiati</th>
                <th className="px-3 py-3">Ross1000</th>
                <th className="px-3 py-3">Ricez.</th>
                <th className="px-3 py-3">Verifica</th>
                <th className="px-3 py-3">Scad. conserv.</th>
                <th className="px-3 py-3">Stato</th>
                <th className="px-3 py-3">File</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((g) => {
                const deleted = g.status === "documento_cancellato";
                const meta = STATUS_META[g.status] || STATUS_META.ricevuto;
                return (
                  <tr key={g.id} data-testid={`gdoc-row-${g.id}`} className="border-b border-slate-100">
                    <td className="px-3 py-2.5 font-medium text-slate-900">{g.guest_name || "—"}</td>
                    <td className="px-3 py-2.5"><Checkbox checked={!!g.received} disabled={deleted} onCheckedChange={(v) => patch(g.id, { received: !!v })} data-testid={`gdoc-received-${g.id}`} /></td>
                    <td className="px-3 py-2.5"><Checkbox checked={!!g.complete} disabled={deleted} onCheckedChange={(v) => patch(g.id, { complete: !!v, verify: true })} data-testid={`gdoc-complete-${g.id}`} /></td>
                    <td className="px-3 py-2.5"><Checkbox checked={!!g.guest_data_ok} disabled={deleted} onCheckedChange={(v) => patch(g.id, { guest_data_ok: !!v, verify: true })} data-testid={`gdoc-dataok-${g.id}`} /></td>
                    <td className="px-3 py-2.5"><Checkbox checked={!!g.alloggiati_sent} disabled={deleted} onCheckedChange={(v) => patch(g.id, { alloggiati_sent: !!v })} data-testid={`gdoc-alloggiati-${g.id}`} /></td>
                    <td className="px-3 py-2.5"><Checkbox checked={!!g.ross1000_sent} disabled={deleted} onCheckedChange={(v) => patch(g.id, { ross1000_sent: !!v })} data-testid={`gdoc-ross-${g.id}`} /></td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{fmt(g.received_at)}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{g.verified_at ? <span title={g.verified_by}>{fmt(g.verified_at)}</span> : "—"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-500"><span className="flex items-center gap-1"><Clock size={11} /> {fmt(g.retention_expires_at)}</span>{deleted && g.deleted_at && <div className="text-[10px] text-slate-400">canc. {fmt(g.deleted_at)}</div>}</td>
                    <td className="px-3 py-2.5"><Badge className={meta.cls}>{meta.label}</Badge></td>
                    <td className="px-3 py-2.5">
                      {deleted ? <span className="text-xs text-slate-400">—</span>
                        : g.file_url ? <a href={g.file_url.startsWith("http") ? g.file_url : fullUrl(g.file_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"><Paperclip size={13} /> apri</a>
                        : <span className="text-xs text-slate-400"><FileCheck size={13} /></span>}
                    </td>
                  </tr>
                );
              })}
              {docs.length === 0 && <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-400">Nessun documento ospite ricevuto (arrivano dal flusso Tally → Make → Urban OS)</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
